import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/context/FavoritesContext";
import { VehicleCard } from "@/components/VehicleCard";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { LogOut, Inbox, Heart, User, Loader2, BellRing, Plus, Trash2, MailWarning } from "lucide-react";
import { toast } from "sonner";
import { PushToggle } from "@/components/PushToggle";

const TYPE_LABELS = {
  achat: "Achat", vente: "Vente/Reprise", recherche: "Recherche perso",
  importation: "Importation", atelier: "RDV Atelier", devis: "Devis", contact: "Contact",
};
const statusColor = { nouveau: "bg-[#C5A059] text-black", "en cours": "bg-blue-500/20 text-blue-300", "traité": "bg-green-500/20 text-green-300" };

function VerifyBanner() {
  const [sending, setSending] = useState(false);
  const resend = async () => {
    setSending(true);
    try { await api.post("/auth/resend-verification"); toast.success("E-mail de vérification renvoyé"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <div data-testid="verify-banner" className="mb-8 flex flex-wrap items-center justify-between gap-3 bg-[#C5A059]/10 border border-[#C5A059]/40 rounded-sm p-4">
      <div className="flex items-center gap-3">
        <MailWarning className="h-5 w-5 text-[#C5A059]" />
        <p className="text-sm text-neutral-200">Vérifiez votre adresse e-mail pour activer les alertes et sécuriser votre compte.</p>
      </div>
      <Button data-testid="resend-verify-btn" onClick={resend} disabled={sending} size="sm" variant="outline" className="border-[#C5A059]/50 text-[#C5A059] hover:bg-[#C5A059]/10 rounded-sm">
        {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Renvoyer l'e-mail
      </Button>
    </div>
  );
}

function AlertsTab({ verified }) {
  const [alerts, setAlerts] = useState(null);
  const [form, setForm] = useState({ brand: "", max_price: "", fuel: "", transmission: "", min_year: "", max_mileage: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/me/alerts").then((r) => setAlerts(r.data)).catch(() => setAlerts([]));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    setSaving(true);
    const payload = {
      brand: form.brand || null,
      max_price: form.max_price ? Number(form.max_price) : null,
      fuel: form.fuel || null,
      transmission: form.transmission || null,
      min_year: form.min_year ? Number(form.min_year) : null,
      max_mileage: form.max_mileage ? Number(form.max_mileage) : null,
    };
    try {
      await api.post("/me/alerts", payload);
      toast.success("Alerte créée — vous serez prévenu par e-mail");
      setForm({ brand: "", max_price: "", fuel: "", transmission: "", min_year: "", max_mileage: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/me/alerts/${id}`); toast.success("Alerte supprimée"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const describe = (c) => {
    const parts = [];
    if (c.brand) parts.push(c.brand);
    if (c.max_price) parts.push(`≤ ${new Intl.NumberFormat("fr-FR").format(c.max_price)} €`);
    if (c.fuel) parts.push(c.fuel);
    if (c.transmission) parts.push(c.transmission);
    if (c.min_year) parts.push(`dès ${c.min_year}`);
    if (c.max_mileage) parts.push(`≤ ${new Intl.NumberFormat("fr-FR").format(c.max_mileage)} km`);
    return parts.length ? parts.join(" • ") : "Tous les nouveaux véhicules";
  };

  if (!verified) {
    return (
      <div className="text-center py-16 border border-white/10 rounded-sm bg-[#121212]">
        <BellRing className="h-10 w-10 text-[#C5A059] mx-auto mb-4" />
        <h3 className="font-display text-xl text-white">Vérifiez votre e-mail pour créer des alertes</h3>
        <p className="text-neutral-400 mt-2">Confirmez votre adresse via le lien reçu, puis revenez ici.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PushToggle />
      <div className="grid gap-8 lg:grid-cols-2">
      <div className="bg-[#0f0f0f] border border-white/10 rounded-sm p-6">
        <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2"><Plus className="h-5 w-5 text-[#C5A059]" /> Nouvelle alerte</h3>
        <p className="text-sm text-neutral-500 mt-1">Laissez un champ vide pour ne pas le filtrer.</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div><Label className="text-neutral-300 mb-1.5 block text-sm">Marque</Label><Input data-testid="alert-brand" value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder="Ex : BMW" className="bg-[#0a0a0a] border-white/15 text-white focus-visible:ring-[#C5A059]" /></div>
          <div><Label className="text-neutral-300 mb-1.5 block text-sm">Budget max (€)</Label><Input data-testid="alert-price" type="number" value={form.max_price} onChange={(e) => set("max_price", e.target.value)} placeholder="30000" className="bg-[#0a0a0a] border-white/15 text-white focus-visible:ring-[#C5A059]" /></div>
          <div>
            <Label className="text-neutral-300 mb-1.5 block text-sm">Énergie</Label>
            <Select value={form.fuel} onValueChange={(v) => set("fuel", v === "any" ? "" : v)}>
              <SelectTrigger data-testid="alert-fuel" className="bg-[#0a0a0a] border-white/15 text-white"><SelectValue placeholder="Peu importe" /></SelectTrigger>
              <SelectContent><SelectItem value="any">Peu importe</SelectItem>{["Essence", "Diesel", "Hybride", "Électrique"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-neutral-300 mb-1.5 block text-sm">Boîte</Label>
            <Select value={form.transmission} onValueChange={(v) => set("transmission", v === "any" ? "" : v)}>
              <SelectTrigger data-testid="alert-transmission" className="bg-[#0a0a0a] border-white/15 text-white"><SelectValue placeholder="Peu importe" /></SelectTrigger>
              <SelectContent><SelectItem value="any">Peu importe</SelectItem>{["Automatique", "Manuelle"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-neutral-300 mb-1.5 block text-sm">Année min.</Label><Input data-testid="alert-year" type="number" value={form.min_year} onChange={(e) => set("min_year", e.target.value)} placeholder="2018" className="bg-[#0a0a0a] border-white/15 text-white focus-visible:ring-[#C5A059]" /></div>
          <div><Label className="text-neutral-300 mb-1.5 block text-sm">Km max.</Label><Input data-testid="alert-mileage" type="number" value={form.max_mileage} onChange={(e) => set("max_mileage", e.target.value)} placeholder="100000" className="bg-[#0a0a0a] border-white/15 text-white focus-visible:ring-[#C5A059]" /></div>
        </div>
        <Button data-testid="create-alert-btn" onClick={create} disabled={saving} className="mt-5 w-full bg-[#C5A059] text-black hover:bg-[#E6C678] rounded-sm">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Créer l'alerte
        </Button>
      </div>

      <div>
        <h3 className="font-display text-lg font-semibold text-white mb-4">Mes alertes actives</h3>
        {alerts === null ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#C5A059]" /></div>
        ) : alerts.length === 0 ? (
          <p className="text-neutral-500 text-sm border border-white/10 rounded-sm bg-[#121212] p-6">Aucune alerte pour le moment. Créez-en une pour être prévenu par e-mail dès qu'un véhicule correspond.</p>
        ) : (
          <div className="space-y-3">
            {alerts.map((a) => (
              <div key={a.id} data-testid={`alert-${a.id}`} className="flex items-center justify-between gap-3 bg-[#121212] border border-white/10 rounded-sm p-4">
                <div className="flex items-center gap-3 min-w-0">
                  <BellRing className="h-5 w-5 text-[#C5A059] shrink-0" />
                  <span className="text-sm text-neutral-200 truncate">{describe(a.criteria || {})}</span>
                </div>
                <button data-testid={`delete-alert-${a.id}`} onClick={() => remove(a.id)} className="text-neutral-500 hover:text-red-400 shrink-0"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

export default function Account() {
  const { user, logout } = useAuth();
  const { count } = useFavorites();
  const navigate = useNavigate();
  const [requests, setRequests] = useState(null);
  const [favorites, setFavorites] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/me/requests").then((r) => setRequests(r.data)).catch(() => setRequests([]));
    api.get("/me/favorites").then((r) => setFavorites(r.data)).catch(() => setFavorites([]));
  }, [count, user]);

  const handleLogout = () => { navigate("/", { replace: true }); logout(); };

  return (
    <div data-testid="account-page" className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-14 w-14 rounded-full object-cover border border-white/15" />
          ) : (
            <div className="h-14 w-14 rounded-full bg-[#C5A059]/15 flex items-center justify-center"><User className="h-6 w-6 text-[#C5A059]" /></div>
          )}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059]">Espace client</p>
            <h1 className="font-display text-2xl font-bold text-white">{user?.name || user?.email}</h1>
          </div>
        </div>
        <Button data-testid="client-logout-btn" onClick={handleLogout} variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-sm"><LogOut className="h-4 w-4 mr-2" /> Déconnexion</Button>
      </div>

      {user && user.email_verified === false && <VerifyBanner />}

      <Tabs defaultValue="requests">
        <TabsList className="bg-[#121212] border border-white/10 mb-8 flex-wrap h-auto">
          <TabsTrigger data-testid="account-tab-requests" value="requests" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-black"><Inbox className="h-4 w-4 mr-2" /> Mes demandes</TabsTrigger>
          <TabsTrigger data-testid="account-tab-favorites" value="favorites" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-black"><Heart className="h-4 w-4 mr-2" /> Mes favoris</TabsTrigger>
          <TabsTrigger data-testid="account-tab-alerts" value="alerts" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-black"><BellRing className="h-4 w-4 mr-2" /> Mes alertes</TabsTrigger>
        </TabsList>

        <TabsContent value="requests">
          {requests === null ? (
            <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#C5A059]" /></div>
          ) : requests.length === 0 ? (
            <div className="text-center py-20 border border-white/10 rounded-sm bg-[#121212]">
              <Inbox className="h-10 w-10 text-[#C5A059] mx-auto mb-4" />
              <h3 className="font-display text-xl text-white">Aucune demande pour le moment</h3>
              <p className="text-neutral-400 mt-2">Déposez une recherche personnalisée ou demandez un devis.</p>
              <Link to="/recherche"><Button className="mt-6 bg-[#C5A059] text-black hover:bg-[#E6C678] rounded-sm">Recherche personnalisée</Button></Link>
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((r) => (
                <div key={r.id} data-testid={`my-request-${r.id}`} className="bg-[#121212] border border-white/10 rounded-sm p-5 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="border-[#C5A059]/40 text-[#C5A059]">{TYPE_LABELS[r.type] || r.type}</Badge>
                      <Badge className={`${statusColor[r.status] || ""} border-0`}>{r.status}</Badge>
                    </div>
                    {r.message && <p className="text-sm text-neutral-400 mt-2 max-w-xl">{r.message}</p>}
                  </div>
                  <span className="text-xs text-neutral-500">{new Date(r.created_at).toLocaleDateString("fr-FR")}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="favorites">
          {favorites === null ? (
            <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#C5A059]" /></div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-20 border border-white/10 rounded-sm bg-[#121212]">
              <Heart className="h-10 w-10 text-[#C5A059] mx-auto mb-4" />
              <h3 className="font-display text-xl text-white">Aucun favori</h3>
              <p className="text-neutral-400 mt-2">Ajoutez des véhicules à vos favoris depuis le catalogue.</p>
              <Link to="/vehicules"><Button className="mt-6 bg-[#C5A059] text-black hover:bg-[#E6C678] rounded-sm">Voir les véhicules</Button></Link>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {favorites.map((v, i) => <Reveal key={v.id} delay={(i % 3) * 0.08}><VehicleCard vehicle={v} /></Reveal>)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="alerts">
          <AlertsTab verified={user?.email_verified !== false} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
